import React, { useState } from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const BookContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const FormSection = styled.div`
  background: var(--bg-card);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  box-shadow: var(--shadow-sm);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  background: var(--bg-input);
  color: var(--text-primary);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const DateTimeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem;
  background: var(--primary-color);
  color: black;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Book: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    origin: '',
    destination: '',
    date: '',
    time: '',
    passengers: 1
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement booking logic
    navigate('/app/activity');
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  return (
    <BookContainer>
      <Header>
        <Title>{t('app.book')}</Title>
      </Header>
      
      <FormSection>
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>
              <FaMapMarkerAlt /> {t('booking.pickup')}
            </Label>
            <Input
              type="text"
              name="origin"
              value={formData.origin}
              onChange={handleChange}
              placeholder={t('booking.pickup')}
              required
            />
          </InputGroup>
          
          <InputGroup>
            <Label>
              <FaMapMarkerAlt /> {t('booking.dropoff')}
            </Label>
            <Input
              type="text"
              name="destination"
              value={formData.destination}
              onChange={handleChange}
              placeholder={t('booking.dropoff')}
              required
            />
          </InputGroup>
          
          <DateTimeContainer>
            <InputGroup>
              <Label>
                <FaCalendarAlt /> {t('booking.date')}
              </Label>
              <Input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </InputGroup>
            
            <InputGroup>
              <Label>
                <FaClock /> {t('booking.time')}
              </Label>
              <Input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
              />
            </InputGroup>
          </DateTimeContainer>
          
          <InputGroup>
            <Label>
              <FaUsers /> {t('booking.passengers')}
            </Label>
            <Input
              type="number"
              name="passengers"
              value={formData.passengers}
              onChange={handleChange}
              min="1"
              max="8"
              required
            />
          </InputGroup>
          
          <SubmitButton
            type="submit"
            disabled={!formData.origin || !formData.destination || !formData.date || !formData.time}
          >
            {t('booking.bookNow')}
          </SubmitButton>
        </Form>
      </FormSection>
    </BookContainer>
  );
};

export default Book; 